(() => {
  class FortuneTailProductMain extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.variantInput = this.querySelector('[data-variant-id]');
      this.optionSelects = [...this.querySelectorAll('[data-option-select]')];
      this.price = this.querySelector('[data-product-price]');
      this.comparePrice = this.querySelector('[data-compare-price]');
      this.saleBadge = this.querySelector('[data-sale-badge]');
      this.availability = this.querySelector('[data-availability]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.addButtonText = this.querySelector('[data-add-to-cart-text]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.purchaseMethodRadios = [...this.querySelectorAll('[data-purchase-method]')];
      this.sellingPlanInput = this.querySelector('[data-selling-plan-id]');
      this.sellingPlanSelect = this.querySelector('[data-selling-plan-select]');
      this.sellingPlanOptions = this.querySelector('[data-selling-plan-options]');
      this.subscriptionBenefits = this.querySelector('[data-subscription-benefits]');
      this.subscriptionPlanName = this.querySelector('[data-subscription-plan-name]');
      this.subscriptionSaving = this.querySelector('[data-subscription-saving]');

      const variantData = this.querySelector('[data-product-variants]');
      try {
        this.variants = JSON.parse(variantData?.textContent || '[]');
      } catch {
        this.variants = [];
      }

      this.currentVariant =
        this.variants.find((variant) => String(variant.id) === this.variantInput?.value) || this.variants[0] || null;

      this.addEventListener('click', this.handleClick);
      this.optionSelects.forEach((select) => select.addEventListener('change', this.handleVariantChange));
      this.purchaseMethodRadios.forEach((radio) => radio.addEventListener('change', this.handlePurchaseMethodChange));
      this.sellingPlanSelect?.addEventListener('change', this.handleSellingPlanChange);
      this.quantityInput?.addEventListener('change', this.normalizeQuantity);
      this.syncSellingPlansForVariant(this.currentVariant);
      this.updatePurchaseMethod(false);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.optionSelects.forEach((select) => select.removeEventListener('change', this.handleVariantChange));
      this.purchaseMethodRadios.forEach((radio) => radio.removeEventListener('change', this.handlePurchaseMethodChange));
      this.sellingPlanSelect?.removeEventListener('change', this.handleSellingPlanChange);
      this.quantityInput?.removeEventListener('change', this.normalizeQuantity);
      this.initialized = false;
    }

    handleClick = (event) => {
      const thumbnail = event.target.closest('[data-media-thumbnail]');
      if (thumbnail) {
        this.selectMedia(thumbnail.dataset.mediaThumbnail);
        return;
      }

      if (event.target.closest('[data-gallery-previous]')) {
        this.navigateMedia(-1);
        return;
      }

      if (event.target.closest('[data-gallery-next]')) {
        this.navigateMedia(1);
        return;
      }

      if (event.target.closest('[data-quantity-minus]')) {
        this.changeQuantity(-1);
      } else if (event.target.closest('[data-quantity-plus]')) {
        this.changeQuantity(1);
      }
    };

    handleVariantChange = () => {
      const selectedOptions = this.optionSelects.map((select) => select.value);
      const variant = this.variants.find(
        (candidate) =>
          candidate.options.length === selectedOptions.length &&
          candidate.options.every((option, index) => option === selectedOptions[index]),
      );

      this.updateVariant(variant);
    };

    handlePurchaseMethodChange = () => {
      this.updatePurchaseMethod();
    };

    handleSellingPlanChange = () => {
      if (this.sellingPlanInput && this.sellingPlanSelect) {
        this.sellingPlanInput.value = this.sellingPlanSelect.value;
      }
      this.updatePurchasePricing();
      this.updateProductUrl();
    };

    updateVariant(variant) {
      if (!variant) {
        this.currentVariant = null;
        this.variantInput.value = '';
        this.setPurchaseState(false, this.addButton?.dataset.unavailableLabel || 'Unavailable', 'Unavailable');
        return;
      }

      this.currentVariant = variant;
      this.variantInput.value = variant.id;
      this.syncSellingPlansForVariant(variant);
      this.updatePurchaseMethod(false);

      const buttonLabel = variant.available
        ? this.addButton?.dataset.addLabel || 'Add to cart'
        : this.addButton?.dataset.soldOutLabel || 'Sold out';
      this.setPurchaseState(variant.available, buttonLabel, variant.available ? 'Available' : 'Currently unavailable');

      if (variant.featured_media_id) this.selectMedia(String(variant.featured_media_id));
      this.updateProductUrl();
    }

    syncSellingPlansForVariant(variant) {
      const allocations = variant?.selling_plan_allocations || [];
      const subscriptionRadio = this.purchaseMethodRadios.find((radio) => radio.value === 'subscription');
      if (subscriptionRadio) subscriptionRadio.disabled = allocations.length === 0;

      if (this.sellingPlanSelect) {
        [...this.sellingPlanSelect.options].forEach((option) => {
          option.disabled = !allocations.some((allocation) => String(allocation.id) === option.value);
        });
      }

      if (!this.isSubscriptionSelected()) return;

      const requestedPlanId = this.sellingPlanSelect?.value || this.sellingPlanInput?.value;
      const selectedAllocation = allocations.find((allocation) => String(allocation.id) === requestedPlanId);
      const allocation = selectedAllocation || allocations[0];

      if (!allocation) {
        const oneTimeRadio = this.purchaseMethodRadios.find((radio) => radio.value === 'one-time');
        if (oneTimeRadio) oneTimeRadio.checked = true;
        return;
      }

      if (this.sellingPlanSelect) this.sellingPlanSelect.value = String(allocation.id);
      if (this.sellingPlanInput) this.sellingPlanInput.value = allocation.id;
    }

    updatePurchaseMethod(updateUrl = true) {
      const subscriptionSelected = this.isSubscriptionSelected();
      const allocations = this.currentVariant?.selling_plan_allocations || [];
      const selectedPlanId = this.sellingPlanSelect?.value || this.sellingPlanInput?.value;
      const allocation =
        allocations.find((candidate) => String(candidate.id) === String(selectedPlanId)) || allocations[0];
      const useSubscription = subscriptionSelected && Boolean(allocation);

      if (this.sellingPlanInput) {
        this.sellingPlanInput.disabled = !useSubscription;
        this.sellingPlanInput.value = useSubscription ? allocation.id : '';
      }
      if (this.sellingPlanSelect && useSubscription) this.sellingPlanSelect.value = String(allocation.id);
      if (this.sellingPlanOptions) this.sellingPlanOptions.hidden = !useSubscription;
      if (this.subscriptionBenefits) this.subscriptionBenefits.hidden = !useSubscription;

      this.updatePurchasePricing();
      if (updateUrl) this.updateProductUrl();
    }

    updatePurchasePricing() {
      if (!this.currentVariant) return;

      const subscriptionSelected = this.isSubscriptionSelected() && !this.sellingPlanInput?.disabled;
      const selectedPlanId = this.sellingPlanInput?.value;
      const allocation = subscriptionSelected
        ? this.currentVariant.selling_plan_allocations?.find(
            (candidate) => String(candidate.id) === String(selectedPlanId),
          )
        : null;
      const pricing = allocation || this.currentVariant;

      if (this.price) this.price.textContent = pricing.price_formatted;
      const hasComparePrice = pricing.compare_at_price > pricing.price;
      if (this.comparePrice) {
        this.comparePrice.hidden = !hasComparePrice;
        this.comparePrice.textContent = hasComparePrice ? pricing.compare_at_price_formatted : '';
      }

      const hasSale = pricing.sale_percentage > 0;
      if (this.saleBadge) {
        this.saleBadge.hidden = !hasSale;
        this.saleBadge.textContent = hasSale ? `Save ${pricing.sale_percentage}%` : '';
      }

      if (this.subscriptionPlanName) this.subscriptionPlanName.textContent = allocation?.name || '';
      if (this.subscriptionSaving) {
        this.subscriptionSaving.hidden = !allocation || !hasSale;
        this.subscriptionSaving.textContent = allocation && hasSale ? `Save ${pricing.sale_percentage}%` : '';
      }
    }

    isSubscriptionSelected() {
      return this.purchaseMethodRadios.some((radio) => radio.value === 'subscription' && radio.checked);
    }

    updateProductUrl() {
      const productUrl = this.dataset.productUrl;
      if (!productUrl || !this.currentVariant || !window.history?.replaceState) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', this.currentVariant.id);
      if (this.isSubscriptionSelected() && this.sellingPlanInput?.value) {
        url.searchParams.set('selling_plan', this.sellingPlanInput.value);
      }
      window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    }

    setPurchaseState(available, buttonLabel, availabilityLabel) {
      if (this.addButton) this.addButton.disabled = !available;
      if (this.addButtonText) this.addButtonText.textContent = buttonLabel;
      if (this.availability) this.availability.textContent = availabilityLabel;
    }

    selectMedia(mediaId) {
      const panels = this.querySelectorAll('[data-media-panel]');
      const thumbnails = this.querySelectorAll('[data-media-thumbnail]');

      panels.forEach((panel) => {
        const selected = panel.dataset.mediaPanel === mediaId;
        panel.hidden = !selected;
        panel.classList.toggle('is-active', selected);
      });

      thumbnails.forEach((thumbnail) => {
        const selected = thumbnail.dataset.mediaThumbnail === mediaId;
        thumbnail.classList.toggle('is-active', selected);
        thumbnail.setAttribute('aria-selected', String(selected));
      });
    }

    navigateMedia(direction) {
      const panels = [...this.querySelectorAll('[data-media-panel]')];
      if (panels.length < 2) return;

      const activeIndex = panels.findIndex((panel) => panel.classList.contains('is-active'));
      const currentIndex = activeIndex >= 0 ? activeIndex : 0;
      const nextIndex = (currentIndex + direction + panels.length) % panels.length;
      this.selectMedia(panels[nextIndex].dataset.mediaPanel);
    }

    changeQuantity(delta) {
      if (!this.quantityInput) return;
      const current = Number.parseInt(this.quantityInput.value, 10) || 1;
      this.quantityInput.value = Math.max(1, current + delta);
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    normalizeQuantity = () => {
      if (!this.quantityInput) return;
      const quantity = Number.parseInt(this.quantityInput.value, 10);
      this.quantityInput.value = Number.isFinite(quantity) ? Math.max(1, quantity) : 1;
    };
  }

  if (!customElements.get('fortune-tail-product-main')) {
    customElements.define('fortune-tail-product-main', FortuneTailProductMain);
  }
})();
